import React from 'react';
import { Box, ButtonV2, Divider, Flex, SelectV1, TextV2 } from '@withjoy/joykit';

import { pxToRem } from '@withjoy/joykit/theme';
import type { CustomerProductReviewFragment } from '../../CustomerReviews.graphql.generated';
import type { UseReviewOrderingReturn } from '../../useReviewSorting';
import { SkeletonText } from '@shared/components/Skeleton';
import { ReviewListItem } from './ReviewListItem';
import { useTranslation } from '@shared/core';

type ReviewListProps = {
  reviews: CustomerProductReviewFragment[];
  hasMoreReviews: boolean;
  onShowMoreReviewsClick?: () => void;
} & UseReviewOrderingReturn;

const Root: React.FC = ({ children }) => {
  return (
    <Box
      marginTop={88}
      width={583}
      maxWidth={'100%'}
    >
      {children}
    </Box>
  );
};

const Header: React.FC = ({ children }) => {
  return (
    <Flex
      justifyContent={'space-between'}
      alignItems="center"
      __css={{
        '[role="combobox"]': {
          width: 170,
          input: {
            paddingRight: 7,
            ' + span > span': {
              margin: 0,
            },
          },
          '[role="listbox"]': {
            minWidth: 'unset',
          },
        },
      }}
    >
      {children}
    </Flex>
  );
};

const Content: React.FC = ({ children }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={9}
      marginTop={[8, null, pxToRem(80)]}
      width={855}
      maxWidth="100%"
    >
      {children}
    </Flex>
  );
};

export const ReviewList = (props: ReviewListProps) => {
  const { reviews, hasMoreReviews, onShowMoreReviewsClick, orderingOptions, currentOrderingOption, handleOnSetReviewOrdering } = props;
  const { t } = useTranslation('catalogRegistry');
  return (
    <Root>
      <Header>
        <TextV2 typographyVariant={['hed6', null, 'display1']}>Reviews</TextV2>
        <SelectV1
          searchable={false}
          options={orderingOptions}
          value={currentOrderingOption}
          onChange={handleOnSetReviewOrdering}
        />
      </Header>
      <Content>
        {reviews.map((review, idx) => {
          return (
            <React.Fragment key={review.id}>
              <ReviewListItem review={review} />

              {idx < reviews.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
        <Flex justifyContent="center">
          {hasMoreReviews && (
            <ButtonV2
              variant="muted"
              onClick={onShowMoreReviewsClick}
            >
              {t('catalogItemCustomerReviewsSection', 'reviewList', 'showMoreButton')()}
            </ButtonV2>
          )}
        </Flex>
      </Content>
    </Root>
  );
};

ReviewList.Skeleton = () => {
  return (
    <Root>
      <Header>
        <SkeletonText
          rows={1}
          skeletonHeight={[34, null, 38]}
          width={127}
        />
        <SkeletonText
          rows={1}
          skeletonHeight={34}
          width={170}
        />
      </Header>
      <Content>
        <ReviewListItem.Skeleton />
        <Divider />
        <ReviewListItem.Skeleton />
        <Divider />
        <ReviewListItem.Skeleton />
        <Divider />
        <ReviewListItem.Skeleton />
      </Content>
    </Root>
  );
};
